/**
 * Belge açma + "belgelerimi kim açtı".
 *
 * Açma: Worker POST /api/files/open. Yetki, yol güvenliği ve erişim günlüğü
 * veritabanında tek işlemde; bu dosya yalnızca dönen 60 saniyelik URL'yi açar.
 * Burada hiçbir kontrol güvenlik değildir.
 *
 * Günlük: müşteri kendi belgelerine yapılan erişimi görür (access_log RLS).
 * Ofis sahibi/yöneticisi ekibinin erişimini Faz 6 ekranında görecek.
 *
 * KALDIRMAK İÇİN: klasörü sil, shell/index.ts satırını çıkar.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS } from './texts.ts';

interface AccessRow {
  actor_name: string;
  firm_name: string;
  document_name: string;
  action: string;
  created_at: string;
}

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function t(k: string): string { return legacy.t(k); }

/**
 * Belgeyi aç. Pencere TIKLAMA ANINDA açılır, adres sonra verilir: await'ten
 * sonra açılan pencereyi tarayıcı açılır pencere sayıp engeller.
 */
async function openDocument(documentId: string, action: 'open' | 'download'): Promise<boolean> {
  const win = window.open('', '_blank');
  try {
    const res = await legacy.postApi('/api/files/open', { document_id: documentId, action });
    const data = await res.json().catch(() => ({})) as { url?: string; error?: string };
    if (!res.ok || !data.url) {
      if (win) win.close();
      legacy.toast(data.error || t('fa_fail'));
      return false;
    }
    if (win) { win.opener = null; win.location.href = data.url; }
    else window.location.href = data.url;
    return true;
  } catch {
    if (win) win.close();
    legacy.toast(t('fa_fail'));
    return false;
  }
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

async function renderLog(): Promise<void> {
  const host = el('fa-cust-log');
  const sb = legacy.sb;
  if (!host || !sb) return;
  const cust = (globalThis as unknown as { atIsCustomer?: () => boolean }).atIsCustomer?.() ?? false;
  if (!cust) { host.innerHTML = ''; return; }

  host.innerHTML = '<div class="card" style="margin-bottom:12px">'
    + '<div class="set">' + esc(t('fa_log_t')) + '</div>'
    + '<div style="font-size:11px;color:var(--t2);margin-bottom:10px;line-height:1.6">' + esc(t('fa_log_d')) + '</div>'
    + '<div id="fa-log-rows" style="font-size:12px;color:var(--t2)">…</div></div>';

  /* Müşteri başkasının belgesini açamaz; RLS bu hesaba yalnızca kendi
     belgelerine yapılan erişimi verir. */
  const res = await sb.from('access_log')
    .select('actor_name,firm_name,document_name,action,created_at')
    .order('created_at', { ascending: false })
    .limit(20);
  const rows: AccessRow[] = (res && res.data) || [];
  const box = el('fa-log-rows');
  if (!box) return;
  if (res && res.error) { box.textContent = t('fa_log_fail'); return; }
  if (!rows.length) { box.textContent = t('fa_log_none'); return; }

  box.innerHTML = rows.map((r) => {
    const who = r.actor_name + (r.firm_name ? ' · ' + r.firm_name : '');
    const verb = r.action === 'download' ? t('fa_downloaded') : t('fa_opened');
    return '<div style="display:flex;gap:10px;align-items:baseline;padding:8px 0;border-bottom:1px solid var(--div)">'
      + '<span style="flex:1;color:var(--txt)"><b style="font-weight:600">' + esc(who) + '</b> '
      + esc(verb) + ' <span translate="no">' + esc(r.document_name) + '</span></span>'
      + '<span style="font-size:10px;white-space:nowrap" translate="no">' + esc(when(r.created_at)) + '</span>'
      + '</div>';
  }).join('');
}

export default defineFeature({
  id: 'file-access',
  label: 'Belge açma ve erişim günlüğü',
  view: null,

  boot: () => { legacy.addTranslations(TEXTS); },

  legacyGlobals: {
    faOpenDocument: (id: unknown) => openDocument(String(id), 'open'),
    faDownloadDocument: (id: unknown) => openDocument(String(id), 'download'),
    faRenderLog: () => renderLog(),
  },

  owns: { tables: ['access_log'] },
});
